import {useEffect, useState} from "react";
import {OverlayTrigger, Popover} from "react-bootstrap";
import {HexColorInput, HexColorPicker} from "react-colorful";
import {FiX} from "react-icons/fi";

import Button from "@/components/button/Button.tsx";
import {ColorPickerProps} from "@/components/colorPicker/ColorPicker.types.ts";
import {IconButton} from "@/components/iconButton/IconButton.tsx";
import {Input} from "@/components/input/Input.tsx";
import {useClickAway} from "@/lib/form/useOutsideClick.tsx";

export const COLOR_PRESETS: string[] = [
  "#999999",
  "#bcc1c5",
  "#4d4d4d",
  "#61bd4f",
  "#f2d600",
  "#ffab4a",
  "#eb5a46",
  "#c377e0",
  "#0079bf",
  "#00c2e0",
  "#51e898",
  "#ff80ce",
];

export const randomColorPreset = () => COLOR_PRESETS[Math.floor(Math.random() * COLOR_PRESETS.length)];

export const ColorPicker = ({
  color_hex,
  onChange,
  title,
  placement,
  withPicker = true,
  show,
  hide,
  disabled,
  children,
}: ColorPickerProps) => {
  const [localColor, setLocalColor] = useState<string>(color_hex);
  const [localShow, setLocalShow] = useState<boolean>(false);
  const done = () => {
    onChange(localColor);
    setLocalShow(false);
    if (hide) hide();
  };
  const ref = useClickAway<HTMLDivElement>(done);

  useEffect(() => {
    if (show !== undefined) setLocalShow(show);
  }, [show, setLocalShow]);

  return (
    <OverlayTrigger
      show={localShow}
      placement={placement || "auto"}
      overlay={
        <Popover>
          <Popover.Body ref={ref} className="container">
            <div className="row mb-3">
              <div className="col d-flex align-items-center justify-content-between">
                {title && <span className="pulseTextBodyBold">{title}</span>}
                <IconButton icon={FiX} variant={"ghost"} size={"fitIcon"} onClick={done} />
              </div>
            </div>
            {withPicker && (
              <div className="row mb-1">
                <div className="col">
                  <HexColorPicker className="w-100" color={localColor} onChange={setLocalColor} />
                </div>
              </div>
            )}
            <div className="row gx-2 gy-1">
              {COLOR_PRESETS.map((color) => (
                <div className="col-2" key={`k-${color}`}>
                  <button
                    type="button"
                    className="btn w-100 h-100"
                    style={{backgroundColor: color, maxHeight: "32px"}}
                    onClick={() => setLocalColor(color)}
                  />
                </div>
              ))}
            </div>
            <div className="row mt-2">
              <div className="col">
                <Input
                  id="colorPickerInput"
                  as={HexColorInput}
                  prefixed={true}
                  color={localColor}
                  onChange={setLocalColor}
                  onKeyUp={(e) => {
                    if (e.key === "Enter") done();
                  }}
                  disabled={disabled}
                />
              </div>
            </div>
          </Popover.Body>
        </Popover>
      }
    >
      {children || (
        <Button
          size={"square"}
          variant="colorPicker"
          onClick={() => setLocalShow(true)}
          style={{backgroundColor: color_hex}}
          disabled={disabled}
        />
      )}
    </OverlayTrigger>
  );
};
